import React, { useState } from 'react';
import { CheckCircle2Icon, SendIcon, MailIcon, LinkedinIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { LogoMark } from '../components/LogoMark';
import { ROLE_OPTIONS, CONTACT_EMAIL, LINKEDIN_LABEL } from '../data/site';
interface ContactProps {
  onDownload: () => void;
}
const TOPICS = ['Resource suggestion', 'Case study idea', 'Framework question', 'General mine geology systems question', 'Collaboration opportunity'];
const EXPECT = ['Jordan reads every message personally', 'No sales pitch — this is a community-building stage', 'Good questions often become future resources or case studies'];
export function Contact({
  onDownload
}: ContactProps) {
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // eslint-disable-next-line no-console
    console.log('contact_question_submitted', {
      source: 'contact_page'
    });
    setSubmitted(true);
  };
  return <>
      <section className="relative w-full overflow-hidden bg-cream py-16 lg:py-24" data-id="element-216">
        <div className="mgs-grid absolute inset-0" aria-hidden data-id="element-217" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8" data-id="element-218">
          <div className="max-w-3xl" data-id="element-219">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-220">
              <LogoMark className="h-3.5 w-3.5" data-id="element-221" /> Contact
            </p>
            <h1 className="mt-3 text-4xl font-black leading-[1.05] tracking-tight text-charcoal sm:text-5xl" data-id="element-222">
              Start a conversation
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-warmgray" data-id="element-223">
              Interested in mine geology systems, operational thinking, or
              practical improvement? Jordan is currently building the MGS
              resource library and community — this form is for questions,
              ideas, case study suggestions and future collaboration
              conversations.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:gap-6" data-id="element-224">
              <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2 text-sm font-bold text-charcoal underline decoration-yellow decoration-2 underline-offset-4" data-id="element-225">
                <MailIcon className="h-4 w-4" data-id="element-226" />
                {CONTACT_EMAIL}
              </a>
              <a href="#linkedin" className="inline-flex items-center gap-2 text-sm font-bold text-charcoal underline decoration-yellow decoration-2 underline-offset-4" data-id="element-227">
                <LinkedinIcon className="h-4 w-4" data-id="element-228" />
                {LINKEDIN_LABEL}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-sand py-20 lg:py-28" data-id="element-229">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:items-start lg:px-8" data-id="element-230">
          <div className="lg:col-span-7" data-id="element-231">
            <div className="rounded-2xl border border-charcoal/10 bg-cream p-6 shadow-card lg:p-8" data-id="element-232">
              {submitted ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-233">
                  <CheckCircle2Icon className="h-12 w-12 text-improvements" data-id="element-234" />
                  <h2 className="mt-4 text-xl font-black text-charcoal" data-id="element-235">
                    Thanks — your question is on its way
                  </h2>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-warmgray" data-id="element-236">
                    Jordan will be in touch. In the meantime, the free framework
                    guide is a good place to start.
                  </p>
                  <Button variant="primary" onClick={onDownload} className="mt-6" data-id="element-237">
                    Download the Free MGS Toolkit
                  </Button>
                </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-238">
                  <div className="grid gap-4 sm:grid-cols-2" data-id="element-239">
                    <div data-id="element-240">
                      <label htmlFor="ct-name" className="mb-1.5 block text-xs font-semibold text-charcoal" data-id="element-241">
                        Name
                      </label>
                      <input id="ct-name" required className="mgs-input" placeholder="Your name" data-id="element-242" />
                    </div>
                    <div data-id="element-243">
                      <label htmlFor="ct-email" className="mb-1.5 block text-xs font-semibold text-charcoal" data-id="element-244">
                        Email
                      </label>
                      <input id="ct-email" type="email" required className="mgs-input" placeholder="you@example.com" data-id="element-245" />
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2" data-id="element-246">
                    <div data-id="element-247">
                      <label htmlFor="ct-role" className="mb-1.5 block text-xs font-semibold text-charcoal" data-id="element-248">
                        Role
                      </label>
                      <select id="ct-role" required className="mgs-input" data-id="element-249">
                        <option value="" data-id="element-250">Select your role…</option>
                        {ROLE_OPTIONS.map(r => <option key={r} data-id="element-251">{r}</option>)}
                      </select>
                    </div>
                    <div data-id="element-252">
                      <label htmlFor="ct-company" className="mb-1.5 block text-xs font-semibold text-charcoal" data-id="element-253">
                        Company / site (optional)
                      </label>
                      <input id="ct-company" className="mgs-input" placeholder="Where you work" data-id="element-254" />
                    </div>
                  </div>
                  <div data-id="element-255">
                    <label htmlFor="ct-topic" className="mb-1.5 block text-xs font-semibold text-charcoal" data-id="element-256">
                      What would you like to discuss?
                    </label>
                    <select id="ct-topic" required className="mgs-input" data-id="element-257">
                      <option value="" data-id="element-258">Choose a topic…</option>
                      {TOPICS.map(t => <option key={t} data-id="element-259">{t}</option>)}
                    </select>
                  </div>
                  <div data-id="element-260">
                    <label htmlFor="ct-msg" className="mb-1.5 block text-xs font-semibold text-charcoal" data-id="element-261">
                      Your message
                    </label>
                    <textarea id="ct-msg" required rows={5} className="mgs-input resize-none" placeholder="Your question, idea or suggestion…" data-id="element-262" />
                  </div>
                  <label className="flex items-start gap-2.5 text-sm text-warmgray" data-id="element-263">
                    <input type="checkbox" defaultChecked className="mt-1 h-4 w-4 accent-charcoal" data-id="element-264" />
                    <span data-id="element-265">
                      Also send me MGS Field Notes — practical resources and
                      case studies.
                    </span>
                  </label>
                  <Button type="submit" variant="primary" className="w-full" data-id="element-266">
                    <SendIcon className="h-4 w-4" data-id="element-267" />
                    Send My Question
                  </Button>
                </form>}
            </div>
          </div>

          <div className="space-y-6 lg:col-span-5" data-id="element-268">
            <div className="rounded-xl border border-charcoal/10 bg-cream p-6 shadow-card" data-id="element-269">
              <h2 className="text-lg font-bold text-charcoal" data-id="element-270">
                What to expect
              </h2>
              <ul className="mt-4 space-y-3" data-id="element-271">
                {EXPECT.map(e => <li key={e} className="flex items-start gap-3 text-sm leading-relaxed text-warmgray" data-id="element-272">
                    <LogoMark className="mt-0.5 h-4 w-4 shrink-0" data-id="element-273" />
                    {e}
                  </li>)}
              </ul>
            </div>
            <div className="rounded-xl border border-charcoal/10 bg-charcoal p-6" data-id="element-274">
              <p className="text-xs font-bold uppercase tracking-widest text-yellow" data-id="element-275">
                Prefer to start reading?
              </p>
              <p className="mt-2 text-sm leading-relaxed text-cream/85" data-id="element-276">
                The free MGS Toolkit covers the whole system — a good first step
                before any conversation.
              </p>
              <Button variant="primary" onClick={onDownload} className="mt-4 w-full" data-id="element-277">
                Download the Free MGS Toolkit
              </Button>
            </div>
            <div className="rounded-xl border border-charcoal/10 bg-cream p-6 shadow-card" data-id="element-278">
              <p className="text-sm leading-relaxed text-warmgray" data-id="element-279">
                Looking for checklists and templates? Browse the{' '}
                <Button to="/resources" variant="ghost" className="px-1 py-0 text-sm font-bold underline decoration-yellow decoration-2 underline-offset-4" data-id="element-280">
                  resource library
                </Button>
              </p>
            </div>
          </div>
        </div>
      </section>
    </>;
}